<template>
  <div
    id="map-container"
    :class="{ 'open-gallery': isMultipleImages() === true }"
  >
    <Search
      @update-search="updateSearch"
      @update-search-model="updateSearchModel"
      @toggle-filter="toggleFilter"
      :items="items"
    />
    <v-btn small id="toggleGps" @click="getLocation()">
      <v-icon small color="grey darken-2"> mdi-crosshairs-gps </v-icon>
    </v-btn>
    <v-navigation-drawer
      v-model="drawer"
      :width="400"
      :disable-resize-watcher="true"
      temporary
      stateless
      hide-overlay
      app
    >
      <router-view
        @close="close"
        @center-to-current="centerMapToCurrent"
        @enlarge-current="enlargeCurrent"
        :index="slideIndex"
        @update-filters="updateFilters"
        :filters="filters"
      ></router-view>
    </v-navigation-drawer>
    <v-main>
      <l-map
        ref="map"
        id="map"
        :zoom.sync="zoom"
        :options="mapOptions"
        :center="center"
        :bounds="bounds"
        :min-zoom="minZoom"
        :max-zoom="maxZoom"
        @moveend="handleMoveend"
      >
        <l-tile-layer
          v-for="tileProvider in tileProviders"
          :key="tileProvider.name"
          :name="tileProvider.name"
          :visible="tileProvider.visible"
          :url="tileProvider.url"
          :attribution="tileProvider.attribution"
          :token="token"
          layer-type="base"
        />
        <l-control-attribution
          :position="attributionPosition"
          :prefix="attributionPrefix"
        />
        <l-control-scale :imperial="imperial" :position="scalePosition" />
        <l-control-layers
          :position="layersPosition"
          :collapsed="false"
          :sort-layers="true"
        />
        <l-control-zoom :position="zoomPosition" />
        <v-marker-cluster ref="clusterRef" :options="clusterOptions">
          <l-marker
            v-for="(marker, i) in visibleMarkers"
            :key="marker.id"
            :data-id="marker.id"
            ref="markerRef"
            :lat-lng.sync="marker.location.latlng"
            :icon.sync="marker.icon"
            :draggable="marker.draggable"
            @click="updateSlideIndex(i, marker)"
          >
            <l-popup v-if="marker.popup" :content="marker.popup" />
          </l-marker>
        </v-marker-cluster>
      </l-map>
    </v-main>
    <v-dialog v-model="activeGalleryItem">
      <div class="vgs" @click="closeModal" v-if="activeGalleryItem">
        <button type="button" class="vgs__close" @click="closeModal">
          &times;
        </button>
        <div v-if="slideIndex !== null" class="vgs__container">
          <img class="vgs__container__img" :src="imageUrl" :alt="alt" />
          <slot></slot>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import {
  LMap,
  LTileLayer,
  LMarker,
  LPopup,
  LControlLayers,
  LControlZoom,
  LControlAttribution,
  LControlScale,
} from "vue2-leaflet";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";
import Search from "../components/Search.vue";

const tileProviders = [
  {
    name: "OpenStreetMap",
    visible: true,
    attribution:
      '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  },
  {
    name: "OpenTopoMap",
    visible: false,
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution:
      'Map data: &copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
  },
];

export default {
  name: "Map",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LControlLayers,
    LControlZoom,
    LControlAttribution,
    LControlScale,
    "l-popup": LPopup,
    "v-marker-cluster": Vue2LeafletMarkerCluster,
    Search,
  },
  computed: {
    visibleMarkers() {
      return this.$store.getters.visibleMarkers(this.filters);
    },
    viewportMarkers() {
      return this.$store.getters.viewportMarkers(this.filters);
    },
    viewportImages() {
      var filter = [];
      /*filter.range = this.filters["range"];
      filter.category = this.filters["category"];
      filter.affiliation = this.filters["affiliation"];*/
      return this.$store.getters.viewportMarkers(filter);
    },
    drawer: {
      get() {
        return this.$store.state.sidenav;
      },
      set(val) {
        this.$store.commit("sidenav", val);
      },
    },
    imageUrl() {
      const img = this.$store.state.currentMarker.media.standard_resolution.url;
      if (typeof img === "string") {
        return img;
      }
      return img.url;
    },
    alt() {
      const img = this.$store.state.currentMarker.media.standard_resolution.url;
      if (typeof img === "object") {
        return img.alt;
      }
      return "";
    },
    fields() {
      if (!this.model) return [];

      return Object.keys(this.model).map((key) => {
        return {
          key,
          value: this.model[key] || "n/a",
        };
      });
    },
    items() {
      return this.entries.map((entry) => {
        const post_title =
          entry.post_title.length > this.descriptionLimit
            ? entry.post_title.slice(0, this.descriptionLimit) + "..."
            : entry.post_title;

        return Object.assign({}, entry, { post_title });
      });
    },
  },
  watch: {
    viewportMarkers() {
      return;
    },
    // watch for search string changes
    search(val) {
      this.entries = [];
      if (!val || val.length < 3) return;

      val = encodeURIComponent(val);
      fetch(
        `${window.mmconfig.siteUri}${this.$mmconfig.rest_ns}/autosuggest?s=${val}`
      )
        .then((res) => res.json())
        .then((res) => {
          this.entries = res;
        })
        .catch((err) => {
          console.err(err);
        });
    },
    // watch for search model changes
    searchmodel(id) {
      if (!id) return;
      this.$store.dispatch("loadItem", id).then(() => {
        this.centerMapToCurrent();
        if (
          this.$route.path !==
          `/${this.$mmconfig.slug}/${this.$store.state.currentMarker.id}`
        )
          this.$router.push({
            path: `/${this.$mmconfig.slug}/${this.$store.state.currentMarker.id}`,
          });
        this.drawer = true;
      });
    },
  },
  data() {
    return {
      activeDialog: false, // show/hide dialog
      tileProviders: tileProviders, // list of tile providers
      token: "", // token if using mapbox
      center: [this.$mmconfig.map.centerLat, this.$mmconfig.map.centerLon], // leaflet initial center position
      bounds: null, // leaflet bounds
      zoom: this.$mmconfig.map.zoom, // leaflet zoom
      minZoom: this.$mmconfig.map.minZoom, // leaflet min zoom
      maxZoom: this.$mmconfig.map.maxZoom, // leaflet max zoom
      imperial: false, // leaflet scale type imperial
      scalePosition: "bottomleft", // leaflet scale position
      layersPosition: "bottomright", // leaflet layers position
      zoomPosition: "bottomright", // leaflet zoom position
      attributionPosition: "bottomright", // leaflet attribution position
      attributionPrefix: "WP Media Map", // leaflet attribution prefix
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true,
      },
      clusterOptions: {
        maxClusterRadius: 100, // maximum cluster radius in px
        removeOutsideVisibleBounds: true, // remove marker cluster outside visible bounds
        chunkedLoading: true, // enable chunked loading
        disableClusteringAtZoom: this.$mmconfig.map.disableClusteringAtZoom,
      },
      slideIndex: null,
      filters: {
        range: [this.$mmconfig.filter.minRange, this.$mmconfig.filter.maxRange], // initial period filter range
        min: this.$mmconfig.filter.minYear, // period filter minimum
        max: this.$mmconfig.filter.maxYear, // period filter maximum
        affiliation: [],
      },
      activeGalleryItem: false,
      descriptionLimit: 60,
      entries: [],
      searchmodel: null, // search model
      search: null, // search string
      loading: true,
    };
  },
  beforeRouteUpdate(to, from, next) {
    if (to.params.id) {
      this.searchmodel = to.params.id;
      this.drawer = true;
    } else {
      this.drawer = false;
    }
    next();
  },
  mounted() {
    // if we already have an id
    if (this.$route.params.id) {
      this.searchmodel = this.$route.params.id;
    }
    // set refs in store on next tick
    this.$nextTick(function () {
      this.$store.state.map = this.$refs.map.mapObject;
      this.$store.state.cluster = this.$refs.clusterRef;
      var bounds = this.$store.state.map.getBounds();
      this.$store.dispatch("loadData", {
        top: bounds._northEast.lng,
        left: bounds._northEast.lat,
        bottom: bounds._southWest.lng,
        right: bounds._southWest.lat,
      }); // dispatch loading
      this.loading = false;
    });
    // add listener for gallery control
    window.addEventListener("keydown", (e) => {
      if (e.keyCode === 37) {
        this.onPrev();
      } else if (e.keyCode === 39) {
        this.onNext();
      } else if (e.keyCode === 27) {
        this.close();
      }
    });
  },
  methods: {
    getLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(this.centerMapTo);
      } else {
        console.error("Geolocation is not supported by this browser.");
      }
    },
    isMultiple() {
      return this.viewportMarkers.length > 1;
    },
    isMultipleImages() {
      return this.viewportImages.length > 1;
    },
    setCurrentMarker: function (marker) {
      if (!this.$store.state.currentMarker) {
        this.$store.state.currentMarker = marker;
      } else {
        this.$store.state.currentMarker.icon.options.className = "";
        var el = document.getElementsByClassName("leaflet-marker-icon active");
        while (el.length) {
          el[0].classList.remove("active");
        }
      }
      this.$store.state.currentMarker = marker;

      if (marker !== null) {
        for (var i = 0; i < this.$refs.markerRef.length; i++) {
          if (
            parseInt(this.$refs.markerRef[i].$attrs["data-id"]) === marker.id
          ) {
            if (this.$refs.markerRef[i].mapObject._icon) {
              this.$refs.markerRef[i].mapObject._icon.classList.add("active");
            }
          }
        }
        //marker.icon.options.className = "active";
        this.search = marker.title;
        this.centerMapToCurrent();

        if (this.$route.path !== `/${this.$mmconfig.slug}/${marker.id}`)
          this.$router.push({ path: `/${this.$mmconfig.slug}/${marker.id}` });
        this.drawer = true;
      } else {
        if (this.$route.path !== `/${this.$mmconfig.slug}/`)
          this.$router.push({ path: `/${this.$mmconfig.slug}/` });
      }
    },
    handleMoveend: function () {
      /* workaround to let store getter for viewport makers update */
      const tmp = this.$store.state.markers;
      this.$store.state.markers = [];
      this.$store.state.markers = tmp;
      //this.slideIndex = null;
      if (this.$store.state.currentMarker !== null) {
        for (var i = 0; i < this.viewportMarkers.length; i++) {
          if (
            this.viewportMarkers[i].id === this.$store.state.currentMarker.id
          ) {
            this.slideIndex = i;
          }
        }
      }
      var bounds = this.$store.state.map.getBounds();
      this.$store.dispatch("loadData", {
        top: bounds._northEast.lng,
        left: bounds._northEast.lat,
        bottom: bounds._southWest.lng,
        right: bounds._southWest.lat,
      }); // dispatch loading
    },
    centerMapTo: function (pos) {
      this.$store.state.map.setView(
        [pos.coords.latitude, pos.coords.longitude],
        this.$mmconfig.map.maxZoom
      );
    },
    centerMapToCurrent: function () {
      for (var i = 0; i < this.$refs.markerRef.length; i++) {
        if (
          parseInt(this.$refs.markerRef[i].$attrs["data-id"]) ===
          this.$store.state.currentMarker.id
        ) {
          this.$store.state.cluster.mapObject.zoomToShowLayer(
            this.$refs.markerRef[i].mapObject,
            () => {
              var center = this.$store.state.map.project(
                this.$store.state.currentMarker.location.latlng
              );
              // define center depending on window width
              // if smaller than sidenav width (400px), pad center x by half of sidenav width
              center =
                Math.max(
                  document.documentElement.clientWidth || 0,
                  window.innerWidth || 0
                ) < 400
                  ? [center.x, center.y]
                  : [center.x - 200, center.y];
              var target = this.$store.state.map.unproject(center);
              this.$store.state.map.setView(target);
            }
          );
        }
      }
    },
    enlargeCurrent() {
      this.activeGalleryItem = true;
      this.drawer = false;
    },
    updateSlideIndex(i, marker) {
      this.slideIndex = i;
      this.setCurrentMarker(marker);
      //this.$refs.gallery.updateThumbails();
      if (this.activeGalleryItem === true) {
        this.activeGalleryItem = false;
      }
      if (this.$route.path !== `/${this.$mmconfig.slug}/${marker.id}`) {
        this.$router.push({ path: `/${this.$mmconfig.slug}/${marker.id}` });
      }
    },
    updateSearch(val) {
      this.search = val;
    },
    updateSearchModel(val) {
      this.searchmodel = val;
    },
    // update filter settings
    updateFilters(filters) {
      this.filters = filters;
    },
    close() {
      this.drawer = null;
      this.search = null;
      if (this.$route.path !== `/${this.$mmconfig.slug}/`)
        this.$router.push({ path: `/${this.$mmconfig.slug}/` });
      this.setCurrentMarker(null);
    },
    closeModal() {
      this.activeGalleryItem = false;
      this.drawer = true;
    },
    onPrev() {
      if (this.slideIndex === null) return;
      if (this.slideIndex > 0) {
        this.slideIndex--;
      } else {
        this.slideIndex = this.viewportMarkers.length - 1;
      }
      this.$store.state.currentMarker = this.viewportImages[this.slideIndex];
      this.$refs.gallery.updateThumbails();
    },
    onNext() {
      if (this.slideIndex === null) return;
      if (this.slideIndex < this.viewportMarkers.length - 1) {
        this.slideIndex++;
      } else {
        this.slideIndex = 0;
      }
      this.$store.state.currentMarker = this.viewportMarkers[this.slideIndex];
      this.$refs.gallery.updateThumbails();
    },
    toggleFilter() {
      if (this.$route.path !== `/${this.$mmconfig.slug}/`)
        this.$router.push({ path: `/${this.$mmconfig.slug}/` });
      this.drawer = !this.drawer;
    },
  },
};
</script>

<style lang="scss">
@import "~leaflet/dist/leaflet.css";
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";

#close {
  margin: 10px;
}
/*.open-gallery #map {
    height: calc(100% - 140px);
  }*/
#map {
  position: fixed;
  height: 100%;
  top: 0;
}
#avatar {
  position: absolute;
  top: 0;
  z-index: 3;
  margin: 10px;
  right: 15px;
}
#toggleSearch {
  position: absolute;
  top: 0;
  z-index: 3;
  margin: 14px;
  left: 80px;
}
#toggleFilter {
  position: absolute;
  top: 0;
  z-index: 3;
  margin: 14px;
  left: 40px;
}
#toggleGps {
  position: absolute;
  bottom: 150px;
  min-width: 31px;
  z-index: 3;
  right: 19px;
  padding: 0;
}
.leaflet-bottom.leaflet-right {
  .leaflet-control:not(.leaflet-control-attribution) {
    margin-right: 20px;
    margin-bottom: 5px;
  }
}

.sidebar-button-container {
  position: absolute;
  z-index: 0;
  top: 8px;
  left: 100%;
}
$black-alpha-80: rgba(0, 0, 0, 0.8);
$black: #000;
$white: #fff;
$radius-large: 12px;
// Breakpoints
$screen-xs: 480px;
$screen-sm: 768px;
$screen-md: 992px;
$screen-lg: 1200px;
// So media queries don't overlap when required, provide a maximum
$screen-xs-max: ($screen-sm - 1);
$screen-sm-max: ($screen-md - 1);
$screen-md-max: ($screen-lg - 1);
@mixin respond-to($media) {
  @if $media==xs {
    @media (max-width: $screen-xs-max) {
      @content;
    }
  } @else if $media==sm {
    @media (min-width: $screen-sm) and (max-width: $screen-sm-max) {
      @content;
    }
  } @else if $media==md {
    @media (min-width: $screen-md) and (max-width: $screen-md-max) {
      @content;
    }
  } @else if $media==lg {
    @media (min-width: $screen-lg) {
      @content;
    }
  }
}
@mixin modal-base() {
  transition: opacity 0.2s ease;
  position: fixed;
  z-index: 9998;
}
@mixin modal-mask() {
  @include modal-base();
  top: 0;
  left: 0;
  width: 100%;
  background-color: $black-alpha-80;
  display: table;
}
/*
.open-gallery .vgs {
  height: calc(100% - 140px);
}
*/
.vgs {
  @include modal-mask();
  //height: calc(100% - 140px) !important;
  height: 100%;
  &__prev,
  &__next,
  &__close {
    position: absolute;
    color: #ffffff !important;
    width: 50px;
    height: 50px;
    z-index: 999;
    background-color: transparent;
    border: none;
    cursor: pointer;
    &:focus {
      outline: 0;
    }
  }
  &__close {
    top: 0;
    right: 0;
    font-size: 25px;
  }
  &__prev,
  &__next {
    top: 50%;
    margin-top: -25px;
    font-size: 40px;
  }
  &__prev {
    left: 0;
  }
  &__next {
    right: 0;
  }
  &__container {
    position: absolute;
    overflow: hidden;
    cursor: pointer;
    overflow: hidden;
    margin: 0 5%;
    left: 0.5%;
    right: 0.5%;
    height: 100%;
    width: 89%;
    border-radius: $radius-large;
    background-color: $black;
    @include respond-to(xs) {
      width: 100%;
      max-width: 100%;
      top: 50%;
      margin-top: -140px;
      left: 0;
      right: 0;
      border-radius: 0;
      height: 280px;
    }
    &__img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

.leaflet-touch .leaflet-bar,
.leaflet-touch .leaflet-control-layers {
  border: none !important;
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14),
    0 1px 5px 0 rgba(0, 0, 0, 0.12) !important;
  border-radius: 4px;
}

#toolbar {
  position: relative;
  z-index: 3;
  margin: 8px;
  .v-toolbar__content {
    width: 100%;
  }
}

.v-navigation-drawer__content {
  scrollbar-width: thin;
}

.leaflet-glyph-icon span.mdi {
  position: fixed;
}

.leaflet-marker-icon.active {
  filter: hue-rotate(120deg);
}
</style>
